/**
 * Settings > Recurring presets (tracker item 76).
 *
 * A preset is a reusable repeat pattern with billing terms, no customer,
 * property, dates or amount. The New recurring schedule page can start from
 * one; the schedule keeps no link to it, so editing or deleting a preset never
 * changes a schedule. ADMIN and OFFICE_MANAGER change presets; other staff see
 * them read-only. Presets from an industry starter pack carry a Starter badge
 * (ADMIN only) with a Keep button.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IRecurringPresetsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageRecurringPresets, canViewRecurringPresets } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { RECURRENCE_ORDINALS, WEEKDAY_NAMES, WEEKDAY_SHORT, describeRecurrence } from '../../utils/recurrence';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass, rowActionClass, rowDangerActionClass } from '../../components/formStyles';
import { StarterMark, useStarterRecords } from '../../components/StarterBadge';
import { Toast } from '../../components/Toast';
import { BILLING_FREQUENCY_LABELS, FREQUENCY_LABELS, INTERVAL_UNITS, billingIntervalUnit, parseInterval } from '../recurringUi';
import { PresetForm, buildPresetRequest, defaultPresetForm, describePreset, presetFormErrors, presetFormFrom } from '../recurringPresetUi';

const ORDINAL_LABELS: Record<number, string> = { 1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', [-1]: 'Last' };
const ROW_TAP = 'min-h-[44px] md:min-h-0';

type Editing = { mode: 'create' } | { mode: 'edit'; preset: types.RecurringPreset } | null;

export function RecurringPresetsPage() {
  const { user } = useAuth();
  const canView = canViewRecurringPresets(user);
  const canManage = canManageRecurringPresets(user);
  const api = useMemo<IRecurringPresetsApi>(() => createAPIClient(), []);
  const starter = useStarterRecords('RECURRING_PRESET');
  const [presets, setPresets] = useState<types.RecurringPreset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<PresetForm>(defaultPresetForm);
  const [touched, setTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setPresets(await api.listRecurringPresets());
  }, [api]);

  useEffect(() => {
    if (!canView) return;
    refresh().catch((err) => setError(getErrorMessage(err, 'The recurring presets could not be loaded.')));
  }, [canView, refresh]);

  const errors = presetFormErrors(form);
  const shown = (field: keyof typeof errors) => (touched ? errors[field] : undefined);
  const setField = <K extends keyof PresetForm>(field: K, value: PresetForm[K]) => setForm((current) => ({ ...current, [field]: value }));

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(defaultPresetForm());
    setTouched(false);
    setFormError(null);
    setConfirmDeleteId(null);
  };
  const openEdit = (preset: types.RecurringPreset) => {
    setEditing({ mode: 'edit', preset });
    setForm(presetFormFrom(preset));
    setTouched(false);
    setFormError(null);
    setConfirmDeleteId(null);
  };
  const closeForm = () => {
    setEditing(null);
    setFormError(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!editing || saving || Object.keys(errors).length > 0) return;
    setSaving(true);
    setFormError(null);
    try {
      const request = buildPresetRequest(form);
      if (editing.mode === 'edit') await api.updateRecurringPreset(editing.preset.id, request);
      else await api.createRecurringPreset(request);
      closeForm();
      await refresh();
    } catch (err) {
      setFormError(getErrorMessage(err, editing.mode === 'edit' ? 'The preset could not be saved.' : 'The preset could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (preset: types.RecurringPreset) => {
    setBusyId(preset.id);
    setError(null);
    try {
      await api.deleteRecurringPreset(preset.id);
      setConfirmDeleteId(null);
      if (editing?.mode === 'edit' && editing.preset.id === preset.id) closeForm();
      await refresh();
    } catch (err) {
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, 'The preset could not be deleted.'));
    } finally {
      setBusyId(null);
    }
  };

  if (!canView) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <p role="alert" className="text-gray-700">You do not have permission to see recurring presets.</p>
      </main>
    );
  }

  const interval = parseInterval(form.interval);
  const unit = INTERVAL_UNITS[form.frequency];
  const flat = form.billingModel === 'FLAT_CONTRACT';

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <Link to="/settings" className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Recurring presets</h1>
        </div>
        {canManage && !editing && (
          <button type="button" onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto`}>
            Add preset
          </button>
        )}
      </div>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        A preset is a repeat pattern and billing terms you can start a new recurring schedule from. It pre-fills the form and nothing is linked
        afterwards, so changing a preset never changes a schedule that was started from it.
      </p>

      {error && <Toast kind="error">{error}</Toast>}

      {canManage && editing && (
        <section className="mb-6 rounded-lg bg-white shadow" aria-labelledby="preset-form-heading">
          <form onSubmit={submit} noValidate>
            <div className="p-4 sm:p-6 space-y-5">
              <h2 id="preset-form-heading" className="text-base font-semibold text-gray-900 break-words">
                {editing.mode === 'edit' ? `Edit ${editing.preset.name}` : 'New recurring preset'}
              </h2>
              {formError && <Toast kind="error">{formError}</Toast>}

              <fieldset disabled={saving} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="preset-name" className={labelClass}>Name<span className="ml-0.5 text-red-600">*</span></label>
                    <input id="preset-name" type="text" maxLength={100} className={inputClass} value={form.name} placeholder="Weekly mowing" onChange={(e) => setField('name', e.target.value)} aria-invalid={shown('name') ? true : undefined} />
                    {shown('name') && <p className="mt-1 text-sm text-red-700">{shown('name')}</p>}
                  </div>
                  <div className="flex items-end">
                    <label htmlFor="preset-active" className="inline-flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-gray-700 sm:pb-2.5">
                      <input id="preset-active" type="checkbox" checked={form.isActive} onChange={(e) => setField('isActive', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                      <span className="font-medium">Active (offered when starting a schedule)</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="preset-frequency" className={labelClass}>Frequency *</label>
                    <select id="preset-frequency" className={inputClass} value={form.frequency} onChange={(e) => setField('frequency', e.target.value as types.RecurringFrequency)}>
                      {types.RECURRING_FREQUENCIES.map((value) => <option key={value} value={value}>{FREQUENCY_LABELS[value]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preset-interval" className={labelClass}>Repeat every</label>
                    <div className="flex items-center gap-2">
                      <input id="preset-interval" type="number" min={1} max={99} step={1} className={`${inputClass} max-w-[7rem]`} value={form.interval} onChange={(e) => setField('interval', e.target.value)} aria-invalid={shown('interval') ? true : undefined} />
                      <span className="text-sm text-gray-700">{interval === 1 ? unit[0] : unit[1]}</span>
                    </div>
                    {shown('interval') && <p className="mt-1 text-sm text-red-700">{shown('interval')}</p>}
                  </div>
                </div>

                {form.frequency === 'WEEKLY' && (
                  <div role="group" aria-labelledby="preset-days-label">
                    <span id="preset-days-label" className={labelClass}>On these days *</span>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAY_SHORT.map((label, day) => (
                        <label key={label} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2.5 sm:py-1.5 text-sm ${form.daysOfWeek.includes(day) ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                          <input
                            type="checkbox"
                            checked={form.daysOfWeek.includes(day)}
                            aria-label={WEEKDAY_NAMES[day]}
                            onChange={() => setField('daysOfWeek', form.daysOfWeek.includes(day) ? form.daysOfWeek.filter((d) => d !== day) : [...form.daysOfWeek, day])}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.frequency === 'MONTHLY' && (
                  <div role="radiogroup" aria-label="Monthly pattern" className="space-y-3">
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <input type="radio" name="preset-monthly-mode" checked={form.monthlyMode === 'DAY_OF_MONTH'} onChange={() => setField('monthlyMode', 'DAY_OF_MONTH')} />
                      On day
                      <input type="number" min={1} max={31} aria-label="Day of the month" className={`${inputClass} max-w-[6rem]`} value={form.dayOfMonth} disabled={form.monthlyMode !== 'DAY_OF_MONTH'} onChange={(e) => setField('dayOfMonth', e.target.value)} />
                      of the month
                    </label>
                    <label className="flex flex-wrap items-center gap-2 text-sm">
                      <input type="radio" name="preset-monthly-mode" checked={form.monthlyMode === 'NTH_WEEKDAY'} onChange={() => setField('monthlyMode', 'NTH_WEEKDAY')} />
                      On the
                      <select aria-label="Which week" className={`${inputClass} max-w-[8rem]`} value={form.ordinal} disabled={form.monthlyMode !== 'NTH_WEEKDAY'} onChange={(e) => setField('ordinal', e.target.value)}>
                        {RECURRENCE_ORDINALS.map((value) => <option key={value} value={String(value)}>{ORDINAL_LABELS[value]}</option>)}
                      </select>
                      <select aria-label="Day of the week" className={`${inputClass} max-w-[10rem]`} value={form.weekday} disabled={form.monthlyMode !== 'NTH_WEEKDAY'} onChange={(e) => setField('weekday', e.target.value)}>
                        {WEEKDAY_NAMES.map((name, day) => <option key={name} value={String(day)}>{name}</option>)}
                      </select>
                      of the month
                    </label>
                  </div>
                )}
                {shown('pattern') && <p role="alert" className="text-sm text-red-700">{shown('pattern')}</p>}

                <div role="radiogroup" aria-label="Billing" className="space-y-2 text-sm">
                  <label className="flex items-start gap-2">
                    <input type="radio" name="preset-billing" className="mt-1" checked={form.billingModel === 'PER_VISIT'} onChange={() => setField('billingModel', 'PER_VISIT')} />
                    <span>Per visit: each generated job is invoiced like any other job</span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="radio" name="preset-billing" className="mt-1" checked={flat} onChange={() => setField('billingModel', 'FLAT_CONTRACT')} />
                    <span>Flat contract: one invoice per billing period. The amount is entered when a schedule is created.</span>
                  </label>
                </div>
                {flat && (
                  <div className="grid grid-cols-1 gap-4 rounded-md border border-gray-200 p-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="preset-billing-period" className={labelClass}>Billing period *</label>
                      <select id="preset-billing-period" className={inputClass} value={form.billingFrequency} onChange={(e) => setField('billingFrequency', e.target.value as types.FlatContractBillingFrequency)}>
                        {types.FLAT_CONTRACT_BILLING_FREQUENCIES.map((value) => <option key={value} value={value}>{BILLING_FREQUENCY_LABELS[value]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="preset-billing-every" className={labelClass}>Bill every</label>
                      <div className="flex items-center gap-2">
                        <input id="preset-billing-every" type="number" min={1} max={120} step={1} className={`${inputClass} max-w-[7rem]`} value={form.billingInterval} onChange={(e) => setField('billingInterval', e.target.value)} aria-invalid={shown('billingInterval') ? true : undefined} />
                        <span className="text-sm text-gray-700">{billingIntervalUnit(form.billingFrequency, Number.isInteger(parseInterval(form.billingInterval)) ? parseInterval(form.billingInterval) : 1)}</span>
                      </div>
                      {shown('billingInterval') && <p className="mt-1 text-sm text-red-700">{shown('billingInterval')}</p>}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="preset-notes" className={labelClass}>Notes</label>
                  <textarea id="preset-notes" rows={2} className={inputClass} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
                  <p className="mt-1 text-xs text-gray-500">Copied into the schedule's notes when you start from this preset.</p>
                </div>
              </fieldset>

              <p className="rounded-md bg-gray-50 p-3 text-sm font-semibold text-gray-900" aria-live="polite">
                {errors.pattern ? 'Pattern incomplete' : describeRecurrence(form.frequency, Number.isInteger(interval) ? interval : 1, buildPresetRequest(form).frequencyDetails ?? undefined)}
              </p>
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={saving} className={`${secondaryButtonClass} w-full sm:w-auto`}>Cancel</button>
              <button type="submit" disabled={saving} className={`${primaryButtonClass} w-full sm:w-auto`}>
                {saving ? 'Saving...' : editing.mode === 'edit' ? 'Save changes' : 'Create preset'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="rounded-lg bg-white shadow p-3 sm:p-6" aria-label="Recurring presets">
        {presets === null && !error && <p role="status" className="py-8 text-center text-gray-600">Loading presets...</p>}
        {presets && presets.length === 0 && (
          <p className="py-8 text-center text-gray-600">
            No recurring presets yet.{' '}
            {canManage ? 'Add one, or apply an industry starter pack from Settings.' : ''}
          </p>
        )}
        {presets && presets.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {presets.map((preset) => {
              const isConfirming = confirmDeleteId === preset.id;
              const busy = busyId === preset.id;
              return (
                <li key={preset.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 break-words">
                    <div className={`flex flex-wrap items-center gap-x-3 font-semibold ${preset.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                      <span>{preset.name}</span>
                      {!preset.isActive && <span className="text-xs font-normal text-gray-500">Inactive</span>}
                      <StarterMark records={starter} entityId={preset.id} name={preset.name} />
                    </div>
                    <div className="text-sm text-gray-600">{describePreset(preset)}</div>
                    {preset.notes && <div className="text-sm text-gray-500">{preset.notes}</div>}
                  </div>
                  {canManage && (
                    <span className="flex shrink-0 flex-wrap items-center gap-x-4">
                      {isConfirming ? (
                        <>
                          <span className="w-full text-sm text-gray-700 sm:w-auto">Delete this preset? Schedules started from it are not affected.</span>
                          <button type="button" className={`${rowDangerActionClass} ${ROW_TAP}`} disabled={busy} onClick={() => void remove(preset)}>{busy ? 'Deleting...' : 'Confirm'}</button>
                          <button type="button" className={`${rowActionClass} ${ROW_TAP}`} disabled={busy} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className={`${rowActionClass} ${ROW_TAP}`} disabled={saving || busyId !== null} aria-label={`Edit ${preset.name}`} onClick={() => openEdit(preset)}>Edit</button>
                          <button type="button" className={`${rowDangerActionClass} ${ROW_TAP}`} disabled={busyId !== null} aria-label={`Delete ${preset.name}`} onClick={() => setConfirmDeleteId(preset.id)}>Delete</button>
                        </>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
